import React, { useEffect, useCallback, useRef } from "react";
import * as XLSX from "xlsx";

/**
 * Specialized Excel Parser Component
 *
 * Handles Excel file parsing (.xlsx, .xls) with multi-sheet support and data transformation.
 *
 * @param {Object} props - Component props
 * @param {File} props.file - The Excel file to parse
 * @param {Object} props.config - Configuration object for parsing
 * @param {Function} props.onDataParsed - Callback when data is successfully parsed
 * @param {Function} props.onError - Callback when parsing fails
 * @param {Function} props.onFileDataUpdate - Callback to update file data for viewer
 * @param {Function} props.onExcelSheetsUpdate - Callback to update Excel sheets data
 */
const ExcelParser = ({
  file,
  config,
  onDataParsed,
  onError,
  onFileDataUpdate,
  onExcelSheetsUpdate,
}) => {
  const processedFileRef = useRef(null);
  const isProcessingRef = useRef(false);

  /**
   * Map Excel data to form structure using configuration
   */
  const mapExcelDataToForm = useCallback(
    (row, headers) => {
      // Always create a result object with all headers, regardless of config
      const result = {};

      // First, map all headers directly to preserve structure
      headers.forEach((header, index) => {
        if (header && header.trim()) {
          const cleanHeader = header.trim();
          let value = row[index];

          // Handle undefined/null values
          if (value === undefined || value === null) {
            value = "";
          }

          result[cleanHeader] = value;
        }
      });

      // If we have config, also try to map to configured fields
      if (config?.columns) {
        let configMappings = 0;

        headers.forEach((header, index) => {
          if (!header || !header.trim()) return;

          const headerStr = String(header).trim();

          const configEntry = Object.entries(config.columns).find(
            ([key, columnConfig]) =>
              columnConfig.possibleHeaders?.some((h) => {
                const possibleHeader = String(h).toLowerCase();
                const currentHeader = headerStr.toLowerCase();
                return possibleHeader === currentHeader;
              })
          );

          if (configEntry) {
            const [key, columnConfig] = configEntry;
            let value = row[index];

            // Handle undefined/null values
            if (value === undefined || value === null) {
              value = "";
            }

            // Apply transformation if provided
            if (
              columnConfig.transform &&
              typeof columnConfig.transform === "function" &&
              value !== ""
            ) {
              try {
                value = columnConfig.transform(value);
              } catch (error) {
                console.warn(`Transform error for ${key}:`, error);
              }
            }

            result[key] = value;
            configMappings++;
          }
        });
      }

      return result;
    },
    [config]
  );

  /**
   * Parse Excel file
   */
  const parseExcel = useCallback(() => {
    if (!file) return;

    // Prevent duplicate processing of the same file
    const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
    if (processedFileRef.current === fileKey || isProcessingRef.current) {
      return;
    }

    processedFileRef.current = fileKey;
    isProcessingRef.current = true;

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        // Process all sheets instead of just the first one
        const sheetsData = [];
        let allExtractedData = [];
        let primarySheetData = null;

        workbook.SheetNames.forEach((sheetName, index) => {
          const worksheet = workbook.Sheets[sheetName];

          // Use sheet_to_json with defval option to preserve empty cells
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: "", // This ensures empty cells are preserved as empty strings
            raw: false, // This ensures consistent string formatting
          });

          if (jsonData.length === 0) {
            // Only warn for non-default sheet names to reduce noise
            if (
              sheetName !== "Sheet1" &&
              sheetName !== "Sheet2" &&
              sheetName !== "Sheet3"
            ) {
              console.warn(`Sheet "${sheetName}" is empty, skipping`);
            }
            return;
          }

          // Only filter out completely empty rows (all cells are null/undefined/empty)
          const filteredData = jsonData.filter((row) => {
            // A row is considered empty only if ALL cells are empty
            return row.some(
              (cell) => cell !== null && cell !== undefined && cell !== ""
            );
          });

          if (filteredData.length === 0) {
            // Only warn for non-default sheet names to reduce noise
            if (
              sheetName !== "Sheet1" &&
              sheetName !== "Sheet2" &&
              sheetName !== "Sheet3"
            ) {
              console.warn(
                `Sheet "${sheetName}" contains no valid data, skipping`
              );
            }
            return;
          }

          // Try to find the actual header row
          let headerRowIndex = 0;
          
          // For ORDERS sheet, always use the first row as header
          if (sheetName === "ORDERS") {
            headerRowIndex = 0;
          } else {
            // For other sheets, detect header by finding row with most non-empty cells
            let maxNonEmptyCells = 0;
            let bestHeaderScore = -1;

            for (let i = 0; i < Math.min(5, filteredData.length); i++) {
              const row = filteredData[i];
              const nonEmptyCells = row.filter(
                (cell) => cell !== null && cell !== undefined && cell !== ""
              ).length;

              // Calculate header score: prefer rows with text-like values
              // Headers are usually text, not numbers
              const textLikeCells = row.filter((cell) => {
                if (cell === null || cell === undefined || cell === "") return false;
                const str = String(cell).trim();
                // Headers are usually short text, not long numbers
                // Check if it looks like text (contains letters, or is a short identifier)
                return (
                  /[a-zA-Z]/.test(str) ||
                  (str.length < 20 && !/^\d+\.?\d*$/.test(str))
                );
              }).length;

              // Score combines: non-empty cells count + text-like cells bonus
              const headerScore = nonEmptyCells + (textLikeCells * 2);

              // Prefer rows with more text-like cells (likely headers)
              // But also need enough non-empty cells
              if (headerScore > bestHeaderScore && nonEmptyCells >= 3) {
                bestHeaderScore = headerScore;
                headerRowIndex = i;
                maxNonEmptyCells = nonEmptyCells;
              }
            }
          }

          const headers = filteredData[headerRowIndex];
          const rows = filteredData.slice(headerRowIndex + 1);

          // Clean and validate headers
          const cleanHeaders = headers.map((header) => {
            if (header === null || header === undefined) {
              return "";
            }
            return String(header).trim();
          });

          // Store sheet data for viewer
          sheetsData.push({
            name: sheetName,
            data: filteredData, // Array of arrays for SpreadsheetViewer
          });

          // Extract and map data from this sheet - DON'T filter out rows with some empty cells
          // This preserves the alignment between headers and data
          const sheetExtractedData = rows
            .map((row, rowIndex) => {
              // Ensure row has exactly the same length as headers
              const normalizedRow = [];

              // Get the maximum length to handle both shorter and longer rows
              const maxLength = Math.max(row.length, cleanHeaders.length);

              for (let i = 0; i < cleanHeaders.length; i++) {
                if (i < row.length) {
                  // Use the actual value from the row, preserving empty strings
                  normalizedRow[i] =
                    row[i] === null || row[i] === undefined ? "" : row[i];
                } else {
                  // Pad with empty string if row is shorter than headers
                  normalizedRow[i] = "";
                }
              }

              // Debug for problematic rows
              if (normalizedRow.length !== cleanHeaders.length) {
                console.warn(
                  `Row ${rowIndex} length mismatch: ${normalizedRow.length} vs ${cleanHeaders.length}`
                );
              }

              const mappedResult = mapExcelDataToForm(
                normalizedRow,
                cleanHeaders
              );
              return mappedResult;
            })
            .filter((mappedRow) => {
              // Only filter out completely empty mapped objects
              const hasData = Object.values(mappedRow).some(
                (value) => value !== null && value !== undefined && value !== ""
              );
              return hasData;
            });

          // Add sheet name to each row for identification
          const sheetDataWithSource = sheetExtractedData.map((row) => ({
            ...row,
            _sheetName: sheetName,
            _sheetIndex: index,
            _rawSheetData: filteredData, // Include raw sheet data for directional searches
          }));

          allExtractedData = allExtractedData.concat(sheetDataWithSource);

          // Use first sheet as primary for backward compatibility
          if (index === 0) {
            primarySheetData = filteredData;
          }
        });

        if (sheetsData.length === 0) {
          throw new Error("Excel file contains no valid sheets with data");
        }

        // Update file data for viewer (use primary sheet for backward compatibility)
        onFileDataUpdate?.(primarySheetData);
        onExcelSheetsUpdate?.(sheetsData);

        // Call success callback with all data
        onDataParsed?.(allExtractedData, allExtractedData);
      } catch (error) {
        console.error("Excel parsing error:", error);
        onError?.(error);
      } finally {
        isProcessingRef.current = false;
      }
    };

    reader.onerror = () => {
      isProcessingRef.current = false;
      onError?.(new Error("Failed to read Excel file"));
    };

    reader.readAsArrayBuffer(file);
  }, [
    file,
    mapExcelDataToForm,
    onDataParsed,
    onError,
    onFileDataUpdate,
    onExcelSheetsUpdate,
  ]);

  // Auto-parse when file is provided
  useEffect(() => {
    if (file) {
      parseExcel();
    }
  }, [file, parseExcel]);

  // Reset processed file ref when file changes
  useEffect(() => {
    if (file) {
      const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
      if (processedFileRef.current !== fileKey) {
        processedFileRef.current = null;
        isProcessingRef.current = false;
      }
    }
  }, [file]);

  // This component doesn't render anything - it's purely functional
  return null;
};

export default ExcelParser;
